import { useMemo, useState } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users } from 'lucide-react';
import { brandsApi } from '@/features/brands/api';
import {
  xamplinApi,
  entityIdOf,
  deriveEventStatus,
  type XpStructureEvent,
} from '@/lib/api/xamplin';
import { queryKeys } from '@/lib/api/queryKeys';
import { usePagination } from '@/lib/hooks/usePagination';
import { Pagination } from '@/components/ui/Pagination';
import { cn } from '@/lib/cn';
import { HelpHint } from '@/features/help/HelpHint';

/**
 * My Schedule (build-v2 G9) — cross-brand view of every event/shift the user
 * runs, grouped by day. Mirrors the prototype `schedule.jsx`: a chronological
 * agenda across brands with venue, time, staffing and status. Paginated by day.
 */
interface ShiftEvent {
  id: number;
  name: string;
  brand: string;
  project: string;
  projectId: number;
  group: string;
  start: number;
  status: 'plan' | 'live' | 'completed';
  stations: number;
  reporters: number;
}
const STATUS_DOT: Record<string, string> = { plan: 'var(--color-xk-accent)', live: '#4dd28b', completed: '#5aa9ff' };

export function SchedulePage() {
  const [scope, setScope] = useState<'upcoming' | 'all'>('all');

  const brandsQ = useQuery({ queryKey: queryKeys.brands.all, queryFn: () => brandsApi.list() });
  const projectsQ = useQuery({ queryKey: queryKeys.xamplin.projects(), queryFn: () => xamplinApi.projects.list() });

  const brands = (brandsQ.data?.items ?? []).filter((b) => b.companyType !== 'Agency');
  const brandName = (companyId: number | null) => brands.find((b) => b.id === companyId)?.name ?? '—';
  const projects = projectsQ.data?.items ?? [];

  const structureQs = useQueries({
    queries: projects.map((p) => ({
      queryKey: queryKeys.xamplin.projectStructure(p.id),
      queryFn: () => xamplinApi.projects.structure(p.id),
    })),
  });

  const events = useMemo<ShiftEvent[]>(() => {
    const out: ShiftEvent[] = [];
    structureQs.forEach((q, i) => {
      const proj = projects[i];
      if (!proj) return;
      const groups = q.data?.structured?.project?.eventGroups ?? [];
      for (const g of groups)
        for (const e of (g.events ?? []) as XpStructureEvent[]) {
          const start = e.startDate ? Date.parse(e.startDate) : NaN;
          if (!Number.isFinite(start)) continue;
          out.push({
            id: entityIdOf(e.id),
            name: e.name ?? 'Event',
            brand: brandName(proj.companyId),
            project: proj.name,
            projectId: proj.id,
            group: g.name ?? '',
            start,
            status: deriveEventStatus(e),
            stations: e.stations?.length ?? 0,
            reporters: e.reporters?.length ?? 0,
          });
        }
    });
    return out.sort((a, b) => a.start - b.start);
  }, [structureQs, projects, brands]);

  const now = Date.now();
  const scoped = scope === 'upcoming' ? events.filter((e) => e.start >= now - 86_400_000) : events;

  // Group by calendar day → array of [dayLabel, events].
  const days = useMemo(() => {
    const map = new Map<string, ShiftEvent[]>();
    for (const e of scoped) {
      const k = new Date(e.start).toISOString().slice(0, 10);
      (map.get(k) ?? map.set(k, []).get(k)!).push(e);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [scoped]);
  const pg = usePagination(days, 10);

  const loading = projectsQ.isLoading || structureQs.some((q) => q.isLoading);

  return (
    <div className="page flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--color-accent-10)] text-[var(--color-xk-accent)]">
          <Calendar className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-[var(--color-xk-lavender)]">
            My Schedule
          </h1>
          <p className="mono text-xs uppercase tracking-wider text-[var(--color-xk-text-muted)]">
            {scoped.length} shifts · {days.length} días · cross-brand
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex overflow-hidden rounded-lg border border-[var(--color-xk-border)]">
            {(['all', 'upcoming'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setScope(s)}
                className={cn(
                  'px-3 py-1.5 text-xs font-semibold capitalize transition-colors',
                  scope === s ? 'bg-[var(--color-xk-accent)] text-white' : 'text-[var(--color-xk-text-muted)]',
                )}
              >
                {s === 'all' ? 'Todos' : 'Próximos'}
              </button>
            ))}
          </div>
          <HelpHint section="schedule" />
        </div>
      </div>

      {loading && <div className="mono text-sm text-[var(--color-xk-text-muted)]">Cargando agenda…</div>}
      {!loading && days.length === 0 && (
        <div className="mono rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-4 py-3 text-sm text-[var(--color-xk-text-muted)]">
          Sin eventos en la agenda.
        </div>
      )}

      <div className="flex flex-col gap-5">
        {pg.pageItems.map(([day, dayEvents]) => (
          <div key={day} className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <h2 className="font-display text-sm font-bold capitalize text-[var(--color-xk-lavender)]">
                {new Date(day + 'T00:00:00Z').toLocaleDateString('es', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  timeZone: 'UTC',
                })}
              </h2>
              <span className="mono text-[11px] text-[var(--color-xk-text-subtle)]">{dayEvents.length}</span>
            </div>
            <div className="flex flex-col gap-1.5">
              {dayEvents.map((e) => (
                <Link
                  key={e.id}
                  to={`/projects/${e.projectId}`}
                  className="flex items-center gap-3 rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-2.5 transition-colors hover:border-[var(--color-xk-accent)]"
                >
                  <span className="h-2 w-2 flex-none rounded-full" style={{ background: STATUS_DOT[e.status] }} />
                  <span className="mono w-16 flex-none text-[11px] text-[var(--color-xk-text-muted)]">
                    {new Date(e.start).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-[var(--color-xk-lavender)]">{e.name}</div>
                    <div className="mono truncate text-[10px] text-[var(--color-xk-text-muted)]">
                      {e.brand} · {e.group}
                    </div>
                  </div>
                  <span className="mono flex flex-none items-center gap-1 text-[10px] text-[var(--color-xk-text-subtle)]">
                    <MapPin className="h-3 w-3" /> {e.stations}
                  </span>
                  <span className="mono flex flex-none items-center gap-1 text-[10px] text-[var(--color-xk-text-subtle)]">
                    <Users className="h-3 w-3" /> {e.reporters}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
      <Pagination {...pg} />
    </div>
  );
}
